import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { CellProps } from 'react-table'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  CustomTable,
  DataColumn,
  PlainHeader,
  StackedCell,
  TableColumn,
  TableEmptyState,
} from 'app/components'
import { AlocacaoDispositivoList } from '../../core/_models'
import { getAlocacoesDispositivo } from '../../core/_requests'
import { linhaApoio, rotuloMaleta, rotuloMotivoFim } from '../../core/rotulos'

/** Histórico de maletas do aparelho, na ordem do backend (`dtInicio` desc). */
export const MaletasAba = ({ idDispositivo }: { idDispositivo: number }) => {
  const { t } = useT()
  const { data, isFetching, isError, refetch } = useQuery(
    ['dispositivo-alocacoes', idDispositivo],
    () => getAlocacoesDispositivo(idDispositivo),
    { refetchOnWindowFocus: false }
  )

  const colunas = useMemo<ReadonlyArray<TableColumn<AlocacaoDispositivoList>>>(
    () => [
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.maleta')}</PlainHeader>,
        id: 'maleta',
        Cell: ({ row }: CellProps<AlocacaoDispositivoList>) => {
          const { aeTitle, modalidade } = row.original
          const dicom = linhaApoio(aeTitle, modalidade)
          return (
            <StackedCell
              primary={rotuloMaleta(t, row.original.telekit)}
              {...(dicom ? { secondary: dicom } : {})}
            />
          )
        },
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.empresa')}</PlainHeader>,
        id: 'empresa',
        Cell: ({ row }: CellProps<AlocacaoDispositivoList>) => (
          <StackedCell primary={row.original.empresa?.nome ?? ''} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.inicio')}</PlainHeader>,
        id: 'dtInicio',
        cellWidth: '150px',
        Cell: ({ row }: CellProps<AlocacaoDispositivoList>) => (
          <DataColumn data={row.original.dtInicio ?? undefined} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.fim')}</PlainHeader>,
        id: 'dtFim',
        cellWidth: '150px',
        Cell: ({ row }: CellProps<AlocacaoDispositivoList>) =>
          row.original.ativo ? (
            <StackedCell primary={t('pagina.dispositivos.maletas.atual')} />
          ) : (
            <DataColumn data={row.original.dtFim ?? undefined} />
          ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.motivo')}</PlainHeader>,
        id: 'motivoFim',
        cellWidth: '150px',
        Cell: ({ row }: CellProps<AlocacaoDispositivoList>) => (
          <StackedCell primary={rotuloMotivoFim(t, row.original.motivoFim)} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.observacao')}</PlainHeader>,
        id: 'observacao',
        hideOnMobile: true,
        Cell: ({ row }: CellProps<AlocacaoDispositivoList>) => (
          <StackedCell primary={row.original.observacao ?? ''} />
        ),
      },
    ],
    [t]
  )

  return (
    <CustomTable<AlocacaoDispositivoList>
      id='kt_table_dispositivo_maletas'
      ariaLabel={t('pagina.dispositivos.aba.maletas')}
      data={data ?? []}
      columns={colunas}
      isLoading={isFetching}
      isError={isError}
      onRetry={refetch}
      notPaginated
      emptyState={<TableEmptyState title={t('pagina.dispositivos.maletas.vazio')} />}
    />
  )
}
